import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarIcon, ChevronDown, X } from 'lucide-react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, subWeeks, subMonths, subQuarters, subDays } from 'date-fns';

// Convert a JS Date (treated as a local-tz instant) → ISO string carrying
// the local timezone offset so the backend's lexicographic string compare
// against UTC-zoned `created_at` ("…+00:00") respects the user's day
// boundary.  Example for UAE (UTC+4): startOfDay → "2026-05-23T00:00:00+04:00"
// which the backend stores `<` than "2026-05-23T00:00:00+00:00" ⇒ today's
// early-morning UAE leads aren't mis-bucketed under yesterday.
function localIsoBoundary(d) {
    if (!d) return null;
    const pad = (n) => String(n).padStart(2, '0');
    const y = d.getFullYear();
    const m = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    const tz = -d.getTimezoneOffset();
    const sign = tz >= 0 ? '+' : '-';
    const tzh = pad(Math.floor(Math.abs(tz) / 60));
    const tzm = pad(Math.abs(tz) % 60);
    return `${y}-${m}-${day}T${hh}:${mm}:${ss}${sign}${tzh}:${tzm}`;
}

const PERIOD_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
];

function getDateRange(period) {
    const now = new Date();
    switch (period) {
        case 'today':
            return { from: startOfDay(now), to: endOfDay(now) };
        case 'yesterday': {
            const y = subDays(now, 1);
            return { from: startOfDay(y), to: endOfDay(y) };
        }
        case 'this_week':
            return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
        case 'last_week': {
            const lw = subWeeks(now, 1);
            return { from: startOfWeek(lw, { weekStartsOn: 1 }), to: endOfWeek(lw, { weekStartsOn: 1 }) };
        }
        case 'this_month':
            return { from: startOfMonth(now), to: endOfMonth(now) };
        case 'last_month': {
            const lm = subMonths(now, 1);
            return { from: startOfMonth(lm), to: endOfMonth(lm) };
        }
        case 'this_quarter':
            return { from: startOfQuarter(now), to: endOfQuarter(now) };
        case 'last_quarter': {
            const lq = subQuarters(now, 1);
            return { from: startOfQuarter(lq), to: endOfQuarter(lq) };
        }
        case 'this_year':
            return { from: startOfYear(now), to: endOfYear(now) };
        default:
            return null;
    }
}

export const PeriodFilter = ({ onChange, dateFields, className = '', defaultPeriod = null }) => {
    const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod);
    const [selectedField, setSelectedField] = useState(dateFields?.[0]?.value || null);
    const [customRange, setCustomRange] = useState({ from: null, to: null });
    const [open, setOpen] = useState(false);
    const [showCustom, setShowCustom] = useState(false);

    // Fire initial filter when defaultPeriod is set
    React.useEffect(() => {
        if (defaultPeriod && defaultPeriod !== 'custom') {
            const range = getDateRange(defaultPeriod);
            if (range && onChange) {
                // `to` is the EXCLUSIVE upper bound = start of the next day,
                // so the backend's `$lt date_to` query never includes any
                // timestamp from the following calendar day in local time.
                const exclusiveTo = startOfDay(new Date(range.to.getTime() + 1000));
                onChange({
                    period: defaultPeriod,
                    date_from: localIsoBoundary(range.from),
                    date_to: localIsoBoundary(exclusiveTo),
                    date_field: dateFields?.[0]?.value || null,
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only on mount

    const handlePeriodSelect = (period) => {
        if (period === 'custom') {
            setShowCustom(true);
            setSelectedPeriod('custom');
            return;
        }
        setShowCustom(false);
        setSelectedPeriod(period);
        const range = getDateRange(period);
        if (range && onChange) {
            const exclusiveTo = startOfDay(new Date(range.to.getTime() + 1000));
            onChange({
                period,
                date_from: localIsoBoundary(range.from),
                date_to: localIsoBoundary(exclusiveTo),
                date_field: selectedField,
            });
        }
        setOpen(false);
    };

    const handleCustomApply = () => {
        if (customRange.from && onChange) {
            const rawTo = customRange.to || customRange.from;
            const exclusiveTo = startOfDay(new Date(endOfDay(rawTo).getTime() + 1000));
            onChange({
                period: 'custom',
                date_from: localIsoBoundary(startOfDay(customRange.from)),
                date_to: localIsoBoundary(exclusiveTo),
                date_field: selectedField,
            });
        }
        setOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedPeriod(null);
        setShowCustom(false);
        setCustomRange({ from: null, to: null });
        if (onChange) onChange(null);
    };

    const handleFieldChange = (newField) => {
        setSelectedField(newField);
        if (selectedPeriod && selectedPeriod !== 'custom') {
            const range = getDateRange(selectedPeriod);
            if (range && onChange) {
                const exclusiveTo = startOfDay(new Date(range.to.getTime() + 1000));
                onChange({
                    period: selectedPeriod,
                    date_from: localIsoBoundary(range.from),
                    date_to: localIsoBoundary(exclusiveTo),
                    date_field: newField,
                });
            }
        } else if (selectedPeriod === 'custom' && customRange.from) {
            if (onChange) {
                const rawTo = customRange.to || customRange.from;
                const exclusiveTo = startOfDay(new Date(endOfDay(rawTo).getTime() + 1000));
                onChange({
                    period: 'custom',
                    date_from: localIsoBoundary(startOfDay(customRange.from)),
                    date_to: localIsoBoundary(exclusiveTo),
                    date_field: newField,
                });
            }
        }
    };

    const displayLabel = useMemo(() => {
        if (!selectedPeriod) return 'Period';
        if (selectedPeriod === 'custom' && customRange.from) {
            const from = format(customRange.from, 'dd MMM');
            const to = customRange.to ? format(customRange.to, 'dd MMM') : from;
            return `${from} - ${to}`;
        }
        return PERIOD_OPTIONS.find(o => o.value === selectedPeriod)?.label || 'Period';
    }, [selectedPeriod, customRange]);

    return (
        <div className={`flex items-center gap-1.5 ${className}`}>
            {dateFields && dateFields.length > 1 && (
                <div className="flex bg-muted rounded-md p-0.5" data-testid="period-filter-field-toggle">
                    {dateFields.map(f => (
                        <button
                            key={f.value}
                            type="button"
                            className={`px-2 py-1 text-xs rounded transition-colors ${selectedField === f.value ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                            onClick={() => handleFieldChange(f.value)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            )}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 gap-1.5 text-xs"
                        data-testid="period-filter-btn"
                    >
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {displayLabel}
                        {selectedPeriod ? (
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label="Clear date filter"
                                data-testid="period-filter-clear-btn"
                                className="inline-flex items-center justify-center h-4 w-4 ml-1 rounded hover:bg-destructive/15 cursor-pointer"
                                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onClick={handleClear}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClear(e); } }}
                            >
                                <X className="h-3 w-3 hover:text-destructive" />
                            </span>
                        ) : (
                            <ChevronDown className="h-3 w-3 ml-0.5" />
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex">
                        <div className="border-r p-1 space-y-0.5 min-w-[140px]">
                            {PERIOD_OPTIONS.map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`w-full text-left px-3 py-1.5 text-xs rounded transition-colors ${selectedPeriod === opt.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                                    onClick={() => handlePeriodSelect(opt.value)}
                                    data-testid={`period-option-${opt.value}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        {showCustom && (
                            <div className="p-2 flex flex-col gap-2">
                                <Calendar
                                    mode="range"
                                    selected={customRange}
                                    onSelect={(range) => setCustomRange(range || { from: null, to: null })}
                                    numberOfMonths={1}
                                    className="text-xs"
                                />
                                <Button size="sm" className="w-full" onClick={handleCustomApply} disabled={!customRange.from}>
                                    Apply
                                </Button>
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
            {selectedPeriod && (
                <Badge variant="secondary" className="text-[10px] h-5">
                    {dateFields?.find(f => f.value === selectedField)?.label || ''}
                </Badge>
            )}
        </div>
    );
};

export default PeriodFilter;
